import { compact, contains, pick, mapObject, result } from 'underscore';
import { BaseSchemaProcessor } from './base-schema-processor';
import { updateOdataOptionsForFinalUrl } from '../util/update-final-url-property-for-filter';
import { updateDeliveryStatusPropertyForFilter } from '../util/update-delivery-status-property-for-filter';
import { addCampaignTypePropertyForFilter } from '../util/update-campaign-type-property-for-filter';

export class AudienceCriterionWithBISchemaProcessor extends BaseSchemaProcessor {
  updateEntitySchemaForFeatures(entitySchema) {
    const updatedSchema = super.updateEntitySchemaForFeatures(entitySchema);
    const {
      IsAIMCampaignEnabled,
      IsProductAudienceForSearchEnabled,
      IsCustomAudienceAllowed,
      IsCombinedListAudienceAllowed,
      IsInMarketAudienceAllowed,
      IsRemarketingSimilarAudienceAllowed,
      IsCustomerListAudienceAllowed,
      IsExperimentsEnabled,
      IsPersonalizedOffersEnabled,
      IsPersonalizedOffersV3Enabled,
    } = this.Permissions;
    const propWhitelist = compact([
      'AudienceCriterion/AudienceId',
      'AudienceCriterion/AudienceName',
      'AudienceCriterion/AudienceType',
      'AudienceCriterion/BidMultiplier',
      'AdGroup/Name',
      'AdGroup/Status',
      'Campaign/Name',
      'Campaign/Status',
      IsExperimentsEnabled && 'Campaign/IsExperiment',
      'BidType',
      'DeliveryStatus',
      'Level',
      'Status',
    ]);
    const biPattern = /^PerformanceMetrics/;

    updatedSchema.properties = pick(
      updatedSchema.properties,
      prop => contains(propWhitelist, prop.property) || biPattern.test(prop.property)
    );

    updatedSchema.properties = mapObject(updatedSchema.properties, prop => ({
      ...prop,
      title: prop.title || this.i18n.getString(`FilterLocalization_DataAttribute_${result(prop.alias, 'legacy') || prop.originalProperty}`),
    }));

    const deliveryStatusEnumSet = [
      'Eligible',
      'CampaignDeleted',
      'CampaignSuspended',
      'CampaignBudgetPaused',
      'AdGroupExpired',
      'AdGroupDraft',
      'AdGroupPrivacyPending',
      'AdGroupTargetingTooNarrow',
      'CampaignUserPaused',
      'AdGroupUserPaused',
      'AdGroupAudienceAssociationPaused',
      'AdGroupAudienceAssociationExcluded',
    ];

    const EnableImpressionBasedList = this.Permissions.dynamic && this.Permissions.dynamic.EnableImpressionBasedList;
    const IsCustomSegmentEnabled = this.Permissions.dynamic && this.Permissions.dynamic.EnableKeywordCustomSegments;
    updateDeliveryStatusPropertyForFilter(entitySchema, deliveryStatusEnumSet);

    const audienceIdProperty = updatedSchema.properties.AudienceCriterionAudienceId;
    audienceIdProperty.skipLocalization = true;

    const audienceTypeProperty = updatedSchema.properties.AudienceCriterionAudienceType;
    if (audienceTypeProperty) {
      // Reject the audience type if the pilot isn't enabled
      audienceTypeProperty.enum = compact([
        'Enum.AudienceType\'Remessaging\'',
        (IsAIMCampaignEnabled || IsProductAudienceForSearchEnabled) && 'Enum.AudienceType\'Product\'',
        IsCustomAudienceAllowed && 'Enum.AudienceType\'Custom\'',
        IsInMarketAudienceAllowed && 'Enum.AudienceType\'InMarket\'',
        IsRemarketingSimilarAudienceAllowed && 'Enum.AudienceType\'RemarketingSimilar\'',
        IsCustomerListAudienceAllowed && 'Enum.AudienceType\'CustomerList\'',
        IsCombinedListAudienceAllowed && 'Enum.AudienceType\'CombinedList\'',
        EnableImpressionBasedList && 'Enum.AudienceType\'ImpressionBasedRemarketing\'',
        IsCustomSegmentEnabled && 'Enum.AudienceType\'CustomSegment\'',
      ]);
    }

    // make campaignstatus and adgroupproperty not show in filter drop down
    updatedSchema.properties.CampaignStatus.filterable = false;
    updatedSchema.properties.AdGroupStatus.filterable = false;

    if (this.isForVNext) {
      const campaignTypeNotSupported = ["Enum.CampaignType'LinkedIn'"];
      addCampaignTypePropertyForFilter({
        entitySchema: updatedSchema, permissions: this.Permissions, campaignTypeNotSupported,
      });
    }

    if (IsPersonalizedOffersEnabled && !IsPersonalizedOffersV3Enabled) {
      updatedSchema.properties.AudienceCriterionCashbackAdjustment = {
        filterable: true,
        minimum: 0,
        maximum: 50,
        type: 'number',
        name: 'CashbackAdjustment',
        property: 'AudienceCriterion/CashbackAdjustment',
        title: this.i18n.getString('FilterLocalization_DataAttribute_CashbackAdjustment'),
        required: true,
      };
    }

    return updatedSchema;
  }

  updateOdataOptions(odataOptions) {
    super.updateOdataOptions(odataOptions);
    updateOdataOptionsForFinalUrl(odataOptions);

    return odataOptions;
  }
}
